import React, { useMemo, useState } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Button, IconButton, Popover, Stack, Typography } from "@mui/material";
import { NumberInput } from "components/forms";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useNavigate } from "hooks/shared-hooks/use-navigate";
import { IApiServiceError } from "store";

import { EditingField } from "./SimpleTable";

type FieldType = "target" | "target_percent" | "actual_investment" | "actual_percentage" | "expected_return";

export interface EditTargetPopupSubmitParams {
  id: number;
  field: FieldType;
  value: number;
  asset_class_type: "custom" | "system";
}

interface EditTargetPopupProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSubmit: (params: EditTargetPopupSubmitParams) => Promise<void>;
  state: EditingField;
  loading: boolean;
  onResetToOriginal: () => void;
}

export const EditTargetPopup: React.FC<EditTargetPopupProps> = ({
  anchorEl,
  onClose,
  onSubmit,
  state,
  loading,
  onResetToOriginal
}) => {
  const [value, setValue] = useState(state.row[state.field] || 0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const navigate = useNavigate();

  const onApply = async () => {
    try {
      if (!state.row.asset_class_id) return;
      await onSubmit({
        id: state.row.asset_class_id,
        field: state.field,
        value,
        asset_class_type: state.row.asset_class_type
      });
    } catch (error) {
      const errorsByField = (error as IApiServiceError).errors?.[state.field];
      if (Array.isArray(errorsByField) && errorsByField.length > 0) {
        setErrorMessage(errorsByField[0]);
      } else {
        setErrorMessage((error as IApiServiceError).message);
      }
    }
  };

  const title = useMemo(() => {
    if (state.field === "target") return "Target";
    if (state.field === "target_percent") return "Target %";
    if (state.field === "actual_investment") return "Actual Investment";
    if (state.field === "actual_percentage") return "Actual %";
    if (state.field === "expected_return") return "Expected return";
    return "";
  }, [state.field]);

  const goToIndexes = () => {
    navigate({ route: "indexes", setQueriesMethod: "replace" });
    onClose();
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left"
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left"
      }}
      PaperProps={{
        sx: {
          borderRadius: "8px"
        }
      }}
    >
      <Stack sx={{ gap: "20px", padding: "24px" }}>
        <Stack direction="row" justifyContent="space-between">
          <Typography fontSize="18px" fontWeight="400" color="#071F35">
            {title}
            {state.field === "expected_return" && state.row.asset_class_type === "custom" && (
              <MaterialTooltip isShow title="Portfolio Construction–specific asset class">
                <IconButton>
                  <InfoOutlinedIcon sx={{ width: "15px", height: "15px" }} />
                </IconButton>
              </MaterialTooltip>
            )}
          </Typography>
          {((state.row.actual_investment_original && state.field === "actual_investment") ||
            (state.row.actual_percentage_original && state.field === "actual_percentage") ||
            (state.row.expected_return_original && state.field === "expected_return")) && (
            <MaterialTooltip
              isShow
              placement="bottom-start"
              sx={{ fontSize: "4px" }}
              title={`Original amount: ${
                state.field === "actual_investment"
                  ? state.row.actual_investment_original?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                      minimumFractionDigits: 0
                    })
                  : state.field === "actual_percentage"
                    ? state.row.actual_percentage_original?.toLocaleString("en-US", {
                        style: "percent",
                        minimumFractionDigits: 2
                      })
                    : state.row.expected_return_original?.toLocaleString("en-US", {
                        style: "percent",
                        minimumFractionDigits: 2
                      })
              }`}
            >
              <Button
                variant="outlined"
                color="primary"
                sx={{ fontSize: "14px", fontWeight: "400", color: "#071F35" }}
                startIcon={<RefreshIcon sx={{ width: "15px", height: "15px" }} />}
                onClick={onResetToOriginal}
              >
                Reset to original
              </Button>
            </MaterialTooltip>
          )}
          {state.field === "expected_return" && state.row.asset_class_type === "system" && (
            <Typography
              fontSize="16px"
              fontWeight="400"
              color="#26A6AE"
              sx={{ cursor: "pointer" }}
              onClick={goToIndexes}
            >
              Edit in Admin Panel
            </Typography>
          )}
        </Stack>
        <NumberInput
          label={title}
          sx={{
            minWidth: "360px",
            "& .MuiFormLabel-root": {
              color: "#496682"
            },
            "& fieldset": {
              border: "1px solid #496682"
            }
          }}
          error={!!errorMessage}
          helperText={errorMessage}
          value={value}
          onChange={(e) => {
            if (errorMessage) setErrorMessage(null);
            setValue(Number(e.target.value));
          }}
          numberInputProps={{
            ...((state.field === "target" || state.field === "actual_investment") && { prefix: "$" }),
            ...((state.field === "target_percent" ||
              state.field === "actual_percentage" ||
              state.field === "expected_return") && { suffix: "%" }),
            allowNegative: false,
            isNumericString: true,
            decimalScale: 2
          }}
        />
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={onApply} variant="contained" loading={loading} disabled={Boolean(errorMessage)}>
            Apply
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
};
